import { createContext, useContext, useEffect, type ComponentType } from 'react';
import type { AnyRoute } from '@tanstack/react-router';
import type { AssistantCommand, WidgetDef } from './assistant.js';

export type ExploreTarget = { to: string; search: Record<string, string> };
export type Tone = 'sage' | 'blue' | 'grey' | 'peach' | 'dark';

export interface QuickAction {
  id: string;
  label: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  /** Rendered inside a small dialog. Call onDone after a successful save. */
  Component: ComponentType<{ onDone: () => void }>;
}

/** Identity of the window that shows a route. Routes with the same key share one window. */
export interface WindowIdentity {
  key: string;
  title: string;
  moduleId: string;
  tone: Tone;
  /** The window's own surface: light glass, or the dark observatory. */
  surface?: 'glass' | 'dark';
}

/** A module's browser contribution: routes, windows, launcher tile, home sections, capture. */
export interface WebModule {
  id: string;
  /** The module's space: its place in the scrubber and the Spaces grid. `category` groups spaces once there are many. */
  nav?: {
    label: string;
    path: string;
    order: number;
    icon: ComponentType<{ className?: string }>;
    tone?: Tone;
    category?: string;
    description?: string;
  };
  /** How the space looks: a hue (0–360) for its scene, and whether the scene is light or dark. */
  space?: { hue: number; mode: 'light' | 'dark' };
  /** One live line about the module for the Spaces grid, e.g. "4 reading · 24 books". A hook. */
  useStatus?: () => string | null;
  /** Assistant commands this module understands. */
  commands?: AssistantCommand[];
  /** Homebase instruments this module offers. */
  widgets?: WidgetDef[];
  routes: (parent: AnyRoute) => AnyRoute[];
  windows?: (pathname: string) => WindowIdentity | null;
  quickActions?: QuickAction[];
  /** Offered by a network explorer: where to look at an entity's connections. */
  explore?: (entityKey: string) => ExploreTarget;
}

const ModulesContext = createContext<WebModule[]>([]);
export const ModulesProvider = ModulesContext.Provider;

/** Web modules registered at build time AND enabled for this workspace. */
export function useEnabledWebModules() {
  return useContext(ModulesContext);
}

/** Link to the connections view of an entity, when a network module is enabled. */
export function useExplore(entityKey: string | null | undefined): ExploreTarget | null {
  const modules = useEnabledWebModules();
  if (!entityKey) return null;
  for (const m of modules) if (m.explore) return m.explore(entityKey);
  return null;
}

// ---- Window context: pages describe themselves to the desktop -------------------------

export interface WindowTool {
  id: string;
  label: string;
  icon?: ComponentType<{ className?: string }>;
  /** Single letter; pressed anywhere in the window (outside text fields) it runs the tool. */
  shortcut?: string;
  pressed?: boolean;
  run: () => void;
}

export interface WindowApi {
  /** Which pane this window occupies; the secondary pane exists only in split view. */
  pane: 'primary' | 'secondary';
  setMeta: (meta: { title?: string; subtitle?: string }) => void;
  setTools: (tools: WindowTool[]) => void;
  /** Element inside the focused window, for sheets that dock within it. */
  portal: HTMLElement | null;
}

const WindowContext = createContext<WindowApi | null>(null);
export const WindowProvider = WindowContext.Provider;

export function useWindowPortal() {
  return useContext(WindowContext)?.portal ?? null;
}

/** Sets the focused window's title (e.g. the book open in the reading room). */
export function useWindowMeta(title: string | undefined, subtitle?: string) {
  const api = useContext(WindowContext);
  useEffect(() => {
    if (api && title) api.setMeta({ title, subtitle });
  }, [api, title, subtitle]);
}

/** Registers the floating tool pills shown above the focused window. */
export function useWindowTools(tools: WindowTool[], deps: unknown[]) {
  const api = useContext(WindowContext);
  useEffect(() => {
    if (!api) return;
    api.setTools(tools);
    return () => api.setTools([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, ...deps]);
}
